import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { AppState } from 'src/app/state';
import { Section, SectionType } from 'src/app/models/section';
import { Subsection } from 'src/app/models/subsection';
import { Language } from 'src/app/models/language';
import { UploadIds, UploadKeys } from 'src/app/models/upload';
import { IMAGE_SIZES } from 'src/app/core/dashboard-constants';
import { removeWhitespaces } from 'src/app/core/custom-form-validators';
import { isThereEmptyTranslation } from '../../../helpers/translation-helper';
import { openDashboardConfirmationDialog } from 'src/app/dashboard/state/dashboard.actions';
import { selectSelectedSection } from 'src/app/dashboard/state/sections/sections.selectors';
import {
  selectSelectedLanguage,
  selectSupportedLanguages,
} from 'src/app/dashboard/state/templates/templates.selectors';
import { setSelectedLanguage } from 'src/app/dashboard/state/templates/templates.actions';
import { selectSelectedSubsection, selectSubsections } from 'src/app/dashboard/state/subsections/subsections.selectors';
import { selectSelectedStudy } from 'src/app/dashboard/state/study/study.selectors';
import {
  createSubsection,
  deleteSubsection,
  setSelectedSubsection,
  updateSubsection,
} from 'src/app/dashboard/state/subsections/subsections.actions';
import { uploadReset } from 'src/app/dashboard/state/upload/upload.actions';
import { DEFAULT_DIMENSION_COLOR } from 'src/app/core/dashboard-constants';

const SUBSECTION_NAME_IDENTIFIER = 'subsection-name';
const SUBSECTION_DESCRIPTION_IDENTIFIER = 'section-graph-summary';

@Component({
  selector: 'dashboard-subsections-list',
  templateUrl: './subsections-list.component.html',
  styleUrls: ['./subsections-list.component.scss'],
})
export class SubsectionsListComponent implements OnInit, OnDestroy {
  @Input() iconUuid: string;
  @Input() isImageToEdit: boolean;
  @Output() subsectionChanged = new EventEmitter();

  subsectionsForm: FormGroup;

  SectionType = SectionType;

  selectedSection: Section;
  subsections: Subsection[];
  selectedSubsection: Subsection;

  supportedLanguages: Language[];
  selectedLanguage: Language;

  imgPath: string;
  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_TEMPLATE;
  IMAGE_SIZES = IMAGE_SIZES;

  SUBSECTION_NAME_IDENTIFIER = SUBSECTION_NAME_IDENTIFIER;

  isEditingSubsection: boolean;
  radioGroup: any;
  sectionTypeLabel: string;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private changeDetectorRef: ChangeDetectorRef,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.subsectionsForm = this.formBuilder.group({
      translations: this.formBuilder.array([]),
      color: [DEFAULT_DIMENSION_COLOR],
      icon: [null],
    });

    this.subscriptions.add(
      this.store.select(selectSelectedSection).subscribe((section) => {
        this.selectedSection = section;
        this.sectionTypeLabel =
          this.selectedSection?.type === SectionType.GENERIC_GRAPH
            ? 'Item'
            : this.selectedSection?.type === SectionType.PROFILE
            ? 'Profile'
            : 'Dimension';
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe(
        (study) =>
          (this.idsForUpload = {
            studyId: study?.id,
          })
      )
    );

    this.subscriptions.add(
      this.store.select(selectSubsections).subscribe((subsections) => (this.subsections = subsections))
    );

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => {
        this.selectedLanguage = language;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedSubsection).subscribe((subsection) => {
        this.selectedSubsection = subsection;
        this.isEditingSubsection = !!this.selectedSubsection;
        this.removeAllTranslationFields();
        if (this.isEditingSubsection) {
          if (this.selectedSubsection?.icon) {
            this.isImageToEdit = true;
            this.imgPath = this.selectedSubsection?.icon;
            this.icon.patchValue(this.selectedSubsection?.icon);
          }
          if (this.selectedSubsection?.color) {
            this.color.setValue(this.selectedSubsection?.color);
          }
        } else {
          this.color.setValue(DEFAULT_DIMENSION_COLOR);
        }
        if (this.supportedLanguages) {
          this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
        }
      })
    );
  }

  get translations(): FormArray {
    return this.subsectionsForm.get('translations') as FormArray;
  }

  get color(): AbstractControl {
    return this.subsectionsForm.get('color') as AbstractControl;
  }

  get icon(): AbstractControl {
    return this.subsectionsForm.get('icon') as AbstractControl;
  }

  removeAllTranslationFields(): void {
    this.translations.clear();
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedSubsection?.translations?.filter(
      (translation) => translation?.language === languageCode
    );

    const subsectionName = this.formBuilder.group({
      id: [filteredTranslations?.length ? filteredTranslations[0]?.id : 0],
      language: [languageCode],
      identifier: [SUBSECTION_NAME_IDENTIFIER],
      translation: [
        filteredTranslations?.length ? filteredTranslations[0]?.translation : '',
        [Validators.required, Validators.maxLength(30), removeWhitespaces],
      ],
    });

    this.translations.push(subsectionName);
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  getSubsectionName(subsection) {
    const filteredTranslations = subsection?.translations?.filter(
      (translation) =>
        translation?.identifier === SUBSECTION_NAME_IDENTIFIER && translation?.language === this.selectedLanguage.code
    );
    return filteredTranslations?.length ? filteredTranslations[0].translation : '';
  }

  saveSubsection() {
    this.subsectionsForm.markAllAsTouched();

    if (isThereEmptyTranslation(this.translations.value, SUBSECTION_NAME_IDENTIFIER)) {
      this.toastrService.error(`Please populate ${this.sectionTypeLabel} Name for all languages`);
      return;
    }

    if (!this.icon.value) {
      this.toastrService.info(`Please come back later to upload ${this.sectionTypeLabel} Icon`);
    }

    if (this.selectedSection?.type === SectionType.DIMENSION && !this.color.value) {
      this.toastrService.info(`Please come back later to select ${this.sectionTypeLabel} Color`);
    }

    if (this.subsectionsForm.invalid) {
      return;
    }

    let subsection: Subsection = {
      ...this.subsectionsForm.value,
      templateSectionId: this.selectedSection.id,
    };

    if (this.isEditingSubsection) {
      const subsectionDescriptionTranslations = this.selectedSubsection?.translations?.filter(
        (translation) => translation.identifier === SUBSECTION_DESCRIPTION_IDENTIFIER
      );
      const allTranslations = [...subsection.translations, ...subsectionDescriptionTranslations];
      subsection = {
        ...subsection,
        id: this.selectedSubsection.id,
        hasDetails: this.selectedSubsection.hasDetails,
        detail: this.selectedSubsection.detail,
        translations: allTranslations,
        bars: [],
        color: this.color.value,
      };
      this.store.dispatch(updateSubsection({ subsection }));
    } else {
      subsection = {
        ...subsection,
        bars: [],
        created: true,
      };
      this.store.dispatch(createSubsection({ subsection }));
    }

    this.resetSubsections();
  }

  resetSubsections() {
    this.radioGroup = null;
    this.subsectionsForm.reset();
    this.removeAllTranslationFields();
    if (this.supportedLanguages) {
      this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
    }
    this.isImageToEdit = false;
    this.imgPath = null;
    this.store.dispatch(uploadReset({ uuid: this.iconUuid }));
    this.color.setValue(DEFAULT_DIMENSION_COLOR);
  }

  setSelectedSubsection(subsection) {
    this.store.dispatch(setSelectedSubsection({ selectedSubsection: subsection }));
    if (this.selectedSection?.type === SectionType.PROFILE || this.selectedSection?.type === SectionType.DIMENSION) {
      this.subsectionChanged.emit(true);
    }
  }

  cancelEditingSubsection() {
    this.store.dispatch(setSelectedSubsection({ selectedSubsection: null }));
    this.resetSubsections();
  }

  removeSubsection(subsection) {
    this.store.dispatch(
      openDashboardConfirmationDialog({
        dialogData: {
          title: `Are you sure you want to delete this ${
            this.sectionTypeLabel[0].toLowerCase() + this.sectionTypeLabel.substr(1)
          }?`,
          onConfirm: deleteSubsection({ subsection }),
        },
      })
    );
    this.resetSubsections();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
